import { useEffect, useState } from 'react';

import {
  Avatar,
  Box,
  Button as MuiButton,
  Card,
  Checkbox,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import Drawer from '@/components/wrapped/Drawer';
import { GroupLabel } from '@/cam-admin/control-panel/bits';
import {
  type ActionItem,
  type Call,
  initials,
} from '@/cam-admin/control-panel/fixtures';

const stop = (e: React.MouseEvent) => e.stopPropagation();

// Compact linked-action row inside the call drawer.
const LinkedActionRow = ({
  action,
  onToggleDone,
  onOpen,
}: {
  action: ActionItem;
  onToggleDone: () => void;
  onOpen: () => void;
}) => (
  <Card variant='listItem' sx={{ px: 1.5, py: 1 }} onClick={onOpen}>
    <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
      <Box sx={{ display: 'flex' }} onClick={stop}>
        <Checkbox
          slotProps={{
            input: { 'aria-label': `Mark ${action.title} as done` },
          }}
          size='small'
          checked={Boolean(action.done)}
          onChange={onToggleDone}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant='body2'>{action.title}</Typography>
        {action.due && (
          <Typography
            variant='caption'
            sx={{
              display: 'block',
              color:
                action.overdue && !action.done
                  ? 'error.main'
                  : 'text.secondary',
            }}
          >
            {action.due}
          </Typography>
        )}
      </Box>
      {action.assignee && (
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: 'custom.neutral.300',
            color: 'common.white',
            fontSize: 11,
          }}
        >
          {initials(action.assignee)}
        </Avatar>
      )}
    </Stack>
  </Card>
);

/**
 * The call detail as a right-anchored drawer (the action drawer pattern applied
 * to calls): the call summary + notes, its linked actions and linked documents,
 * with an inline note editor. The call and action data live in ControlPanel,
 * which renders this over the whole frame.
 */
const CallDrawer = ({
  open,
  call,
  linkedActions,
  documents,
  editing,
  onClose,
  onEdit,
  onSaveNotes,
  onDiscardEdit,
  onToggleActionDone,
  onOpenAction,
  onCreateLinkedAction,
  onAddDocument,
  onRemoveDocument,
  onDelete,
  notesOnly = false,
}: {
  open: boolean;
  call?: Call;
  linkedActions: ActionItem[];
  documents: string[];
  editing: boolean;
  onClose: () => void;
  onEdit: () => void;
  onSaveNotes: (text: string) => void;
  onDiscardEdit: () => void;
  onToggleActionDone: (id: string) => void;
  onOpenAction: (id: string) => void;
  onCreateLinkedAction: () => void;
  onAddDocument: () => void;
  onRemoveDocument: (index: number) => void;
  // Delete this note — shown alongside Edit when provided (descoping call notes).
  onDelete?: () => void;
  // Just the note: hide linked actions and documents, and trim the header to the
  // date (descoping call notes).
  notesOnly?: boolean;
}) => {
  const [draft, setDraft] = useState('');

  // The note draft follows the call and the edit toggle. Documents live on the
  // call (persisted by ControlPanel), so they survive the Edit toggle.
  useEffect(() => {
    if (open) setDraft(call?.notes ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, call?.id, editing]);

  if (!call) return null;

  const meta = notesOnly
    ? [call.date]
    : [
        `${call.date} · ${call.time}${call.duration ? ` · ${call.duration}` : ''}${
          call.type ? ` · ${call.type}` : ''
        }`,
        `with ${call.withWho}`,
      ];
  const before = linkedActions.filter((a) => a.callPhase === 'before');
  const after = linkedActions.filter((a) => a.callPhase !== 'before');

  const docList = documents.map((d, i) => (
    <Card key={d} sx={{ px: 2, py: 1 }}>
      <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
        <Box sx={{ color: 'primary.main', display: 'flex' }}>
          <Icon icon={'file-lines' as never} fontSize={18} />
        </Box>
        <Typography sx={{ flex: 1, minWidth: 0 }} variant='body1' noWrap>
          {d}
        </Typography>
        <IconButton
          size='small'
          aria-label={`Remove ${d}`}
          onClick={() => onRemoveDocument(i)}
        >
          <Icon icon={'trash' as never} fontSize={16} />
        </IconButton>
      </Stack>
    </Card>
  ));

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={call.title}
      subtitle={
        <Stack spacing={0.5}>
          {meta.map((m) => (
            <Typography key={m} variant='body1' color='text.secondary'>
              {m}
            </Typography>
          ))}
        </Stack>
      }
      footer={
        editing ? (
          <Stack direction='row' spacing={3}>
            <MuiButton
              variant='outlined'
              color='primary'
              fullWidth
              onClick={onDiscardEdit}
            >
              Discard
            </MuiButton>
            <Button
              variant='primary'
              size='md'
              isFullWidth
              isDisabled={!draft.trim()}
              ariaLabel='Save call summary'
              onPress={() => onSaveNotes(draft.trim())}
            >
              Save
            </Button>
          </Stack>
        ) : onDelete ? (
          <Stack direction='row' spacing={3}>
            <MuiButton
              variant='outlined'
              color='error'
              fullWidth
              onClick={onDelete}
            >
              Delete
            </MuiButton>
            <Button
              variant='primary'
              size='md'
              isFullWidth
              ariaLabel='Edit call note'
              onPress={onEdit}
            >
              Edit
            </Button>
          </Stack>
        ) : (
          <Button
            variant='primary'
            size='md'
            isFullWidth
            ariaLabel='Edit call summary'
            onPress={onEdit}
          >
            Edit
          </Button>
        )
      }
    >
      <Stack spacing={3}>
        {/* summary notes */}
        {editing ? (
          <TextField
            slotProps={{ inputLabel: { shrink: true } }}
            label='Summary notes'
            variant='filled'
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            multiline
            minRows={6}
            fullWidth
          />
        ) : call.notes ? (
          <Card>
            <Box sx={{ p: 3 }}>
              <Typography variant='body1' color='text.secondary'>
                {call.notes}
              </Typography>
            </Box>
          </Card>
        ) : (
          <Typography variant='body2' color='text.secondary'>
            No notes yet. This call hasn’t happened.
          </Typography>
        )}

        {/* linked actions */}
        {!notesOnly && (
          <Stack spacing={1.5}>
            <Stack
              sx={{ alignItems: 'center', justifyContent: 'space-between' }}
              direction='row'
              spacing={2}
            >
              <Typography variant='subtitle2'>Linked actions</Typography>
              <MuiButton
                variant='text'
                color='primary'
                onClick={onCreateLinkedAction}
              >
                Create action
              </MuiButton>
            </Stack>
            {linkedActions.length === 0 && (
              <Typography variant='body2' color='text.secondary'>
                There are no linked actions yet.
              </Typography>
            )}
            {before.length > 0 && (
              <Stack spacing={1}>
                <GroupLabel>Due before the call</GroupLabel>
                {before.map((a) => (
                  <LinkedActionRow
                    key={a.id}
                    action={a}
                    onToggleDone={() => onToggleActionDone(a.id)}
                    onOpen={() => onOpenAction(a.id)}
                  />
                ))}
              </Stack>
            )}
            {after.length > 0 && (
              <Stack spacing={1}>
                <GroupLabel>Due after the call</GroupLabel>
                {after.map((a) => (
                  <LinkedActionRow
                    key={a.id}
                    action={a}
                    onToggleDone={() => onToggleActionDone(a.id)}
                    onOpen={() => onOpenAction(a.id)}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        )}

        {/* linked documents */}
        {!notesOnly && (
          <Stack spacing={1.5}>
            <Typography variant='subtitle2'>Linked documents</Typography>
            <MuiButton
              variant='outlined'
              color='primary'
              fullWidth
              sx={{ borderStyle: 'dashed' }}
              onClick={onAddDocument}
            >
              Upload document
            </MuiButton>
            {docList}
          </Stack>
        )}
      </Stack>
    </Drawer>
  );
};

export default CallDrawer;
